<template>
  <div class="order-entry-quick-info">
    <div v-if="fractionalSymbol">
      <v-dropdown
        class="quick-icon"
        no-caret
        role="navigation"
        boundary="window"
        detached
        detachedClass="order-entry-quick-info"
      >
        <template slot="button-content">
          <span aria-hidden="true">F</span>
          <span class="sr-only">{{ i18n('fButton') }}</span>
        </template>
        <b-dropdown-text>
          <div class="info-header">
            <div class="text">{{ i18n('fDescription') }}</div>
          </div>
        </b-dropdown-text>
      </v-dropdown>
    </div>
    <div v-if="!this._.isEmpty(this.holdingsData)">
      <v-dropdown
        ref="holdingsInfo"
        class="quick-icon"
        no-caret
        role="navigation"
        boundary="window"
        detached
        detachedClass="order-entry-quick-info"
      >
        <template slot="button-content">
          <span aria-hidden="true">H</span>
          <span class="sr-only">{{ i18n('hButton') }}</span>
        </template>
        <b-dropdown-text>
          <div class="info-header">
            <div class="text">{{ i18n('titleHoldings') }} {{ ticker }} {{ country }}</div>
            <c-widget-header-button type="close" @click="onClick" />
          </div>
        </b-dropdown-text>
        <b-dropdown-text>
          <b-table-lite
            striped
            hover
            small
            bordered
            sticky-header
            :fields="holdingsFields"
            :items="holdingsData"
            :aria-label="i18n('titleHoldings')"
          >
            <template v-slot:head()="data">
              {{ i18n('holdingsHeaders.' + data.field.key) }}
            </template>
          </b-table-lite>
        </b-dropdown-text>
      </v-dropdown>
    </div>
    <div v-if="!this._.isEmpty(this.ordersData)">
      <v-dropdown
        ref="orderStatusInfo"
        class="quick-icon"
        no-caret
        role="navigation"
        boundary="window"
        detached
        detachedClass="order-entry-quick-info"
      >
        <template slot="button-content">
          <span aria-hidden="true">O</span>
          <span class="sr-only">{{ i18n('oButton') }}</span>
        </template>
        <b-dropdown-text>
          <div class="info-header">
            <div class="text">{{ i18n('titleOrders') }} {{ ticker }} {{ country }}</div>
            <c-widget-header-button type="close" @click="onClick" />
          </div>
        </b-dropdown-text>
        <b-dropdown-text>
          <b-table-lite
            striped
            hover
            small
            bordered
            sticky-header
            :fields="orderFields"
            :items="ordersData"
            :aria-label="i18n('titleOrders')"
          >
            <template v-slot:head()="data">
              {{ i18n('ordersHeaders.' + data.field.key) }}
            </template>
          </b-table-lite>
        </b-dropdown-text>
      </v-dropdown>
    </div>
  </div>
</template>

<script>
import { toSymbol } from '@state/api';
import TDAPI from '@state/td';
import orderUtil from '@mixins/tdOrdersUtils.js';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';

export default {
  name: 'w-order-entry-quick-info',
  mixins: [orderUtil],
  components: {
    CWidgetHeaderButton
  },
  data() {
    return {
      ticker: undefined,
      country: undefined,
      allHoldings: [],
      orders: undefined,
      holdingsFields: ['symbol', 'quantity', 'avgcost', 'mktval', 'unrealpl'],
      orderFields: ['symbol', 'status', 'quantity', 'remqty', 'priceinfo'],
      fractionalSymbol: false
    };
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    ric: {
      required: true
    }
  },
  computed: {
    account() {
      return this.$store.state.widget[this.wid].accountId;
    },
    holdingsData() {
      if (this.ticker && this.allHoldings) {
        const filteredHoldings = this.allHoldings.filter(holding => {
          if (holding.symbol) {
            return (
              (holding.symbol.tickerSymbol === this.ticker || holding.symbol.companySymbol === this.ticker) &&
              this.country === holding.symbol.market
            );
          }
          return false;
        });
        return filteredHoldings.map(holding => {
          return {
            symbol: holding.symbol.tickerSymbol,
            quantity: this.$f(holding.quantity, 'float.fractionalQuantity'),
            avgcost: this.$f(holding.averageCostPerUnit, 'float.price'),
            mktval: this.$f(holding.marketValue, 'float.price'),
            unrealpl: this.$f(holding.unrealizedGainOrLoss, 'float.price')
          };
        });
      }
      return [];
    },
    ordersData() {
      if (this.ticker && this.orders) {
        return this.orders
          .filter(order => order.security.symbol === this.ticker)
          .map(order => {
            let quantity = '-';
            let remqty = '-';
            if (order.quantity) {
              quantity = this.$f(order.quantity, 'float.fractionalQuantity');
              remqty = this.$f(order.extendedTerms.remainingQuantity, 'float.fractionalQuantity');
            } else if (order.dollarAmt) {
              quantity = '$' + this.$f(order.dollarAmt, 'float.fractionalQuantity');
              remqty = '$' + this.$f(order.extendedTerms.remainingDollarAmt, 'float.fractionalQuantity');
            }
            return {
              symbol: order.security.displayTicker,
              status: this.getStatus(order).displayStatus,
              quantity,
              remqty,
              priceinfo: order.strategy === 'MLO' ? this.getInfoText(order) : this.getPriceInfoText(order)
            };
          });
      }
      return [];
    },
    isFractionalTradingAllowed() {
      return this.$store.getters['accounts/isFractionalTradingAllowed'];
    }
  },
  beforeMount() {
    this.triggerHoldingsFetch();
  },
  methods: {
    i18n(subkey, ns) {
      return this.$t(`white-label.${ns || 'widgets.orderEntry.quickDialog'}.${subkey}`);
    },
    onClick() {
      if (this.$refs.holdingsInfo) {
        this.$refs.holdingsInfo.hide(true);
      }
      if (this.$refs.orderStatusInfo) {
        this.$refs.orderStatusInfo.hide(true);
      }
    },
    triggerHoldingsFetch(isSymbolChange = false) {
      const legsArray = Object.values(this.$store.state.widget[this.wid].legs);
      const isNonEmptySymbolOrSymChange = legsArray.some(leg => leg && leg.symbol.ticker !== '') || isSymbolChange;
      // Position call should be triggered only if there's a symbol in one of the legs
      // or there is a symbole change in OE
      if (this.account && isNonEmptySymbolOrSymChange) {
        this.$store.dispatch('accounts/fetchPositionsFromTD', this.account).then(isSuccess => {
          if (isSuccess) {
            this.allHoldings = this.$store.getters['accounts/getAccountPositions'](this.account);
          }
        });
      }
    },
    triggerOrdersFetch() {
      if (this.account && this.ticker) {
        const params = {
          accountId: this.account,
          status: 'OPEN',
          symbol: this.ticker
        };
        TDAPI.get('/waw/brk/wb/services/rest/v3/orders', { params })
          .then(response => response.data)
          .then(orders => {
            this.getProcessedActiveOrders(orders).then(processedOrders => (this.orders = processedOrders));
          })
          .catch(error => {
            console.error(error);
            this.orders = [];
          });
      }
    },
    clearEntries() {
      this.ticker = undefined;
      this.country = undefined;
      this.fractionalSymbol = false;
    }
  },
  watch: {
    ric: {
      immediate: true,
      handler() {
        if (this.ric && this.ric.length) {
          toSymbol(this.ric, true)
            .then(resp => {
              let data = resp[0];

              if (data.ticker !== this.ric) {
                [this.ticker, this.country] = data.ticker.split(':');
                this.triggerOrdersFetch();
                // Symbol change should trigger both positions and orders call
                this.triggerHoldingsFetch(true);
              } else {
                this.clearEntries();
              }

              //fractional code
              if (this.isFractionalTradingAllowed) {
                if (data.fractionalBuy === 'Y' || data.fractionalSell === 'Y') {
                  this.fractionalSymbol = true;
                  this.$emit('fractional-symbol', true);
                } else {
                  this.fractionalSymbol = false;
                  this.$emit('fractional-symbol', false);
                }
              }
              //END of REGION
            })
            .catch(this.clearEntries);
        } else {
          this.clearEntries();
          this.$emit('fractional-symbol', false);
        }
      }
    },
    account: {
      handler(val) {
        if (val) {
          this.triggerHoldingsFetch();
          this.triggerOrdersFetch();
        }
      }
    }
  }
};
</script>

<style lang="scss">
.order-entry-quick-info {
  display: flex;

  .quick-icon {
    display: inline-flex;
    background-color: $neutral-darken-1;
    border-radius: calc-em(14px);
    width: 2em;
    height: 2em;
    margin-left: calc-em(8px);
    font-weight: 600;
    cursor: pointer;

    .btn-secondary,
    .btn-secondary:not(:disabled):not(.disabled):active,
    .btn-secondary:not(:disabled):not(.disabled).active,
    .btn-secondary:hover,
    .btn-secondary.hover {
      border: 0;
      padding: 0;
      background: transparent !important;
      color: $black-text !important;
      box-shadow: 0 0 0 0 !important;
      -webkit-box-shadow: 0 0 0 0 !important;
    }
  }

  .b-dropdown-text {
    display: flex;
    padding: 0;
    font-weight: 400;
  }
  .b-table-sticky-header {
    margin-bottom: 0px !important;
    th {
      color: $gray-100 !important;
      background-color: $gray-500 !important;
    }
  }
  .info-header {
    display: flex;
    flex-grow: 1;
    .text {
      flex-grow: 1;
      padding: 3px 0px 3px 7px;
    }
  }
}
</style>
